import {useXTerm} from "react-xtermjs";
import React, {useCallback, useEffect, useRef} from "react";
import {useAudioPlayer} from "react-use-audio-player";
import laptop from "../assets/images/laptop.png";
import APPS from "../config/apps";
import {WebLinksAddon} from 'xterm-addon-web-links';
import {FitAddon} from '@xterm/addon-fit';


const red = text => `\x1b[1;31m${text}\x1b[37m`;
const blue = text => `\x1b[1;34m${text}\x1b[37m`;
const green = text => `\x1b[1;32m${text}\x1b[37m`;

const PROMPT = "artem@artembo.com:~# ";
const RADIO_URL = "https://icecast.radiofrance.fr/fip-midfi.mp3?id=radiofrance";
const EMAIL_REGEX = /^\S+@\S+\.\S+$/;
const EMAIL_PROMPT = "Email (optional, press Enter to skip): ";
const APP_PROMPT = `App (${APPS.map(app => app.id).join(" / ")}, Enter to skip): `;

const commands = {
    help: `Commands: ${red("help")}, ${red("about")}, ${red("apps")}, ${red("contact")}, ${red("company")}, ${red("feedback")}`,
    about: "I'm a software engineer based in Novi Sad, Serbia",
    apps: APPS.filter(app => app.url).map(app => `${blue(app.name + ":")} ${app.url}`).join("\r\n"),
    contact: `${blue("Email:")} artem@artembo.com\r\n` +
        `${blue("GitHub:")} https://github.com/artembo\r\n` +
        `${blue("LinkedIn:")} https://www.linkedin.com/in/artembo/\r\n` +
        `${blue("Telegram:")} https://t.me/artembo`,
    whoami: "You are a wonderful person",
    company: `${blue("Name:")} Artem Morozov pr Računarsko programiranje Novi Sad\r\n` +
        `${blue("PIB:")} 113974458\r\n` +
        `${blue("MB:")} 67228758\r\n` +
        `${blue("Address:")} Bulevar Cara Lazara 44 L41, 21000, Novi Sad, Serbia`
}

// feedback and radio are handled by the component itself: feedback starts an
// interactive flow and radio needs the audio player. radio is a secret command,
// so it is excluded from help and tab completion.
const completions = [...Object.keys(commands), "feedback"];

const completeCommand = (command) => {
    const matches = completions.filter(key => key.startsWith(command));
    if (matches.length === 1) {
        return matches[0];
    }
    return "";
}

// how fast the welcome message is typed out, ms per character
const TYPE_DELAY_MS = 7;

// command names rendered in the terminal become clickable and run on click
const CLICKABLE_COMMANDS = [...Object.keys(commands), "feedback", "radio"];

const findCommandLinks = (line, lineNumber) => {
    const links = [];
    for (const name of CLICKABLE_COMMANDS) {
        let index = line.indexOf(name);
        while (index !== -1) {
            const before = line[index - 1];
            const after = line[index + name.length];
            const wholeWord = (before === undefined || !/[a-z0-9]/i.test(before))
                && (after === undefined || !/[a-z0-9]/i.test(after));
            if (wholeWord) {
                links.push({
                    text: name,
                    range: {start: {x: index + 1, y: lineNumber}, end: {x: index + name.length, y: lineNumber}},
                    decorations: {pointerCursor: true, underline: true},
                });
            }
            index = line.indexOf(name, index + name.length);
        }
    }
    return links;
}

const welcomeMessage = `
┌─┐┬─┐┌┬┐┌─┐┌┬┐┌┐ ┌─┐ ┌─┐┌─┐┌┬┐\n\r├─┤├┬┘ │ ├┤ │││├┴┐│ │ │  │ ││││\n\r┴ ┴┴└─ ┴ └─┘┴ ┴└─┘└─┘o└─┘└─┘┴ ┴\n\r${commands.help}
`;


const execute = (command) => {
    return commands[command] || `bash: ${command}: command not found`
}

// resolves user input to a known app id, otherwise passes it through as-is
const matchApp = (input) => {
    const query = input.toLowerCase();
    const match = APPS.find(app => app.id.toLowerCase().startsWith(query) || app.name.toLowerCase().startsWith(query));
    return match ? match.id : input;
}

const sendFeedback = async (app, message, email) => {
    let response;
    try {
        response = await fetch("/api/feedback", {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({app, message, email}),
            signal: AbortSignal.timeout(15000),
        });
    } catch (error) {
        throw new Error("Network error, please try again later.");
    }
    if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Something went wrong, please try again later.");
    }
}

const Laptop = () => {
    const {instance, ref} = useXTerm();
    const [buffer, setBuffer] = React.useState({value: "", command: "", mode: "shell", message: "", app: "", exec: ""});
    const modeRef = useRef("shell");
    const fitAddonRef = useRef(null);
    const playingRef = useRef(false);
    const welcomeDoneRef = useRef(false);
    const lastExecRef = useRef(0);
    const {load, stop, cleanup} = useAudioPlayer();

    // stop the radio stream when the laptop unmounts (route change,
    // resize to the simplified version) - otherwise it plays forever
    useEffect(() => cleanup, [cleanup]);

    const toggleRadio = useCallback(() => {
        if (playingRef.current) {
            stop();
            playingRef.current = false;
            return "Radio stopped.";
        }
        load(RADIO_URL, {autoplay: true, html5: true, format: "mp3"});
        playingRef.current = true;
        return green("♪ FIP radio is on the air. Type radio again to turn it off.");
    }, [load, stop])

    useEffect(() => {
        modeRef.current = buffer.mode;
        if (buffer.command === "newline") {
            instance.write("\r\n");
            const line = buffer.value.trim();
            if (buffer.mode === "shell") {
                if (line === "radio") {
                    instance.writeln(toggleRadio());
                    instance.write(PROMPT);
                    setBuffer({value: "", command: "", mode: "shell", message: "", app: ""});
                } else if (line === "feedback") {
                    instance.writeln("Share your feedback and I'll read it soon.");
                    instance.writeln("Press Enter on an empty message to cancel.");
                    instance.write(blue(APP_PROMPT));
                    setBuffer({value: "", command: "", mode: "feedback-app", message: "", app: ""});
                } else {
                    if (line.length > 0) {
                        instance.writeln(execute(line));
                    }
                    instance.write(PROMPT);
                    setBuffer({value: "", command: "", mode: "shell", message: "", app: ""});
                }
            } else if (buffer.mode === "feedback-app") {
                instance.write(blue("Message: "));
                setBuffer(buffer => ({...buffer, value: "", command: "", mode: "feedback-message", app: line.length > 0 ? matchApp(line) : "terminal"}));
            } else if (buffer.mode === "feedback-message") {
                if (line.length === 0) {
                    instance.writeln("Cancelled.");
                    instance.write(PROMPT);
                    setBuffer({value: "", command: "", mode: "shell", message: "", app: ""});
                } else if (line.length > 4000) {
                    // mirrors the server limit so the composed message isn't lost to a 400
                    instance.writeln(red("Message is too long (max 4000 chars)."));
                    instance.write(blue("Message: "));
                    setBuffer(buffer => ({...buffer, value: "", command: ""}));
                } else {
                    instance.write(blue(EMAIL_PROMPT));
                    setBuffer(buffer => ({...buffer, value: "", command: "", mode: "feedback-email", message: line}));
                }
            } else if (buffer.mode === "feedback-email") {
                if (line.length > 0 && (line.length > 200 || !EMAIL_REGEX.test(line))) {
                    instance.writeln(red("That doesn't look like an email."));
                    instance.write(blue(EMAIL_PROMPT));
                    setBuffer(buffer => ({...buffer, value: "", command: ""}));
                } else {
                    instance.writeln("Sending...");
                    setBuffer(buffer => ({...buffer, value: "", command: "", mode: "busy"}));
                    sendFeedback(buffer.app || "terminal", buffer.message, line)
                        .then(() => instance.writeln(green("Thanks! Your message has been sent.")))
                        .catch(error => instance.writeln(red(error.message)))
                        .finally(() => {
                            instance.write(PROMPT);
                            setBuffer({value: "", command: "", mode: "shell", message: "", app: ""});
                        });
                }
            }
        } else if (buffer.command === "exec") {
            // a command name was clicked: run it like typed input. If something
            // is already typed, abandon that line and start a fresh prompt -
            // erasing is unreliable across wrapped lines and wide characters
            if (buffer.mode === "shell") {
                if (buffer.value.length > 0) {
                    instance.write("\r\n" + PROMPT);
                }
                instance.write(buffer.exec);
                setBuffer(buffer => ({...buffer, value: buffer.exec, command: "newline"}));
            } else {
                setBuffer(buffer => ({...buffer, command: ""}));
            }
        } else if (buffer.command === "tab") {
            if (buffer.mode === "shell") {
                const completed = completeCommand(buffer.value);
                if (completed.length > 0) {
                    for (let i = 0; i < buffer.value.length; i++) {
                        instance.write("\b \b")
                    }
                    instance.write(completed)
                    setBuffer(buffer => ({...buffer, value: completed, command: "type"}))
                }
            }
        } else if (buffer.command === "backspace") {
            instance.write("\b \b")
        }
    }, [buffer, instance, toggleRadio]);
    useEffect(() => {
        if (instance) {
            instance.loadAddon(new WebLinksAddon());
            fitAddonRef.current = new FitAddon();
            instance.loadAddon(fitAddonRef.current);
            const applySize = () => {
                // scale the font with the actual laptop size (see styles.laptop height),
                // not just the window height - on portrait windows width is the limit
                const laptopHeight = Math.min(window.innerHeight * 0.82, window.innerWidth * 0.56);
                instance.options.fontSize = Math.max(14, Math.round(laptopHeight / 37));
                fitAddonRef.current.fit();
            }
            applySize();
            window.addEventListener("resize", applySize);
            // command names in the output run on click, like typing them;
            // the provider is disposed together with the terminal instance
            instance.registerLinkProvider({
                provideLinks: (lineNumber, callback) => {
                    // no links until the welcome message is fully typed out,
                    // and only at the shell prompt
                    if (!welcomeDoneRef.current || modeRef.current !== "shell") {
                        callback(undefined);
                        return;
                    }
                    const bufferLine = instance.buffer.active.getLine(lineNumber - 1);
                    if (!bufferLine) {
                        callback(undefined);
                        return;
                    }
                    const links = findCommandLinks(bufferLine.translateToString(true), lineNumber).map(link => ({
                        ...link,
                        activate: (event, text) => {
                            // xterm fires activate on every mouseup over the link,
                            // so a double click would run the command twice
                            const now = Date.now();
                            if (now - lastExecRef.current < 500) {
                                return;
                            }
                            lastExecRef.current = now;
                            setBuffer(buffer => ({...buffer, command: "exec", exec: text}));
                        },
                    }));
                    callback(links.length > 0 ? links : undefined);
                }
            });
            // write welcome message with a delay and then show the prompt
            for (let i = 0; i < welcomeMessage.length; i++) {
                setTimeout(() => {
                    instance.write(welcomeMessage[i])
                    if (i === welcomeMessage.length - 1) {
                        instance.write("\r\n");
                        instance.write("artem@artembo.com:~# ");
                        welcomeDoneRef.current = true;
                    }
                }, i * TYPE_DELAY_MS)
            }

            instance.onData(key => {
                if (modeRef.current === "busy") {
                    return;
                }
                const charCode = key.charCodeAt(0)
                if (charCode === 127) {
                    setBuffer(buf => ({...buf, value: buf.value.length > 0 ? buf.value.slice(0, -1) : buf.value, command: buf.value.length > 0 ? "backspace" : "null"}))
                } else if (charCode === 9) {
                    setBuffer(buf => ({...buf, value: buf.value, command: "tab"}))
                } else if (charCode === 13) {
                    setBuffer(buf => ({...buf, value: buf.value, command: "newline"}))
                } else if (key === '\x1B[A' || key === '\x1B[B' || key === '\x1B[C' || key === '\x1B[D') {
                    // Ignore arrow keys
                } else {
                    instance.write(key)
                    setBuffer(buf => ({...buf, value: buf.value + key, command: "type"}))
                }
            });
            return () => window.removeEventListener("resize", applySize);
        }
    }, [instance]); // The effect will run only once when the `instance` is initialized.

    return <div style={styles.laptop}>
        <div style={styles.screen}>
            <div ref={ref} style={styles.terminal}/>
        </div>
    </div>
}

const styles = {
    laptop: {
        position: "absolute",
        bottom: "5%",
        left: "50%",
        transform: "translateX(-50%)",
        // as tall as possible while the width still fits the viewport
        height: "min(82vh, 56vw)",
        // the real proportions of laptop.png, so the screen area
        // percentages below match the image exactly
        aspectRatio: "1588 / 1080",
        backgroundImage: `url(${laptop})`,
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
    },
    // the transparent display hole of laptop.png (measured: x 11.65%-88.29%,
    // y 8.15%-78.15%), slightly overshot so no background shows through
    screen: {
        position: "absolute",
        left: "11.5%",
        top: "8%",
        width: "77%",
        height: "70.4%",
        background: "#101420", // matches the xterm theme background
        padding: "12px",
        boxSizing: "border-box",
    },
    terminal: {
        width: "100%",
        height: "100%",
    }
};

export default Laptop
